// src/components/ImageWithFallback.js
// Versión mejorada:
// - inicia el <img> con src inicial (prop `src`) para forzar la petición del navegador.
// - mantiene la lista de candidatos y hace preloads en background.
// - añade onError en el <img> para avanzar al siguiente candidato si el navegador no puede cargar la imagen.
// - conserva fallback final.

import React, { useEffect, useRef, useState } from "react";

let noImageLocal;
try {
  // eslint-disable-next-line global-require
  noImageLocal = require("../assets/no-image.png");
  if (noImageLocal && noImageLocal.default) noImageLocal = noImageLocal.default;
} catch (e) {
  noImageLocal = null;
}

const PUBLIC_NO_IMAGE = (process.env.PUBLIC_URL || "") + "/no-image.png";

// Preload via Image()
function testImage(url, timeout = 4000) {
  return new Promise((resolve) => {
    if (!url) return resolve(false);
    try {
      const img = new Image();
      let done = false;
      const finish = (ok) => {
        if (done) return;
        done = true;
        try { img.onload = img.onerror = null; } catch (e) {}
        resolve(!!ok);
      };
      img.onload = () => finish(true);
      img.onerror = () => finish(false);
      img.src = url;
      setTimeout(() => finish(false), timeout);
    } catch (e) {
      resolve(false);
    }
  });
}

export default function ImageWithFallback({
  src = null,
  candidates = null,
  alt = "",
  className = "",
  style = {},
  fallback = null,
  onLoad: propsOnLoad,
  onError: propsOnError,
  ...rest
}) {
  const preferredFallback = fallback || (noImageLocal ? noImageLocal : PUBLIC_NO_IMAGE);
  const imgRef = useRef(null);

  // Build ordered list: src first, then candidates, then fallbacks
  const buildOrder = () => {
    const out = [];
    if (src) out.push(String(src));
    if (Array.isArray(candidates)) {
      candidates.forEach((c) => {
        if (c && !out.includes(String(c))) out.push(String(c));
      });
    }
    if (!out.includes(preferredFallback)) out.push(preferredFallback);
    if (!out.includes(PUBLIC_NO_IMAGE)) out.push(PUBLIC_NO_IMAGE);
    return Array.from(new Set(out.filter(Boolean)));
  };

  const order = buildOrder();

  // start with src (if provided) so the browser requests it immediately
  const [currentIdx, setCurrentIdx] = useState(() => {
    const initial = order && order.length ? order.indexOf(String(src)) : -1;
    return initial >= 0 ? initial : (order && order.length ? 0 : -1);
  });
  const [currentSrc, setCurrentSrc] = useState(() => (currentIdx >= 0 && order[currentIdx]) ? order[currentIdx] : preferredFallback);

  // Background preloading to find the first working candidate (best-effort)
  useEffect(() => {
    let mounted = true;
    let aborted = false;

    (async () => {
      // Recompute order in case props changed
      const ord = buildOrder();
      // try sequentially but do not overwrite a success already set by user interaction/onError
      for (let i = 0; i < ord.length; i += 1) {
        if (aborted) break;
        const u = ord[i];
        // skip if currentSrc already equals this candidate (already loaded)
        if (mounted && imgRef.current && imgRef.current.src === u) {
          // already loaded by the browser
          break;
        }
        try {
          const ok = await testImage(u, 3500);
          if (ok) {
            if (!mounted) return;
            // only set if we are not already on a better candidate
            setCurrentSrc((prev) => {
              // if prev is a non-fallback and loaded, keep it; otherwise set u
              const isPrevFallback = prev === preferredFallback || prev === PUBLIC_NO_IMAGE || !prev;
              return isPrevFallback ? u : prev;
            });
            return;
          }
        } catch (e) {
          // ignore and try next
        }
      }
      // if none succeeded, ensure we render a fallback
      if (mounted) {
        setCurrentSrc((prev) => (prev ? prev : preferredFallback));
      }
    })();

    return () => {
      aborted = true;
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, Array.isArray(candidates) ? candidates.join("|") : String(candidates || "")]);

  // Update currentSrc/index when props.src or candidates change
  useEffect(() => {
    const ord = buildOrder();
    const i = ord.indexOf(String(src));
    const newIdx = i >= 0 ? i : 0;
    setCurrentIdx(newIdx);
    setCurrentSrc(ord[newIdx] || preferredFallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, Array.isArray(candidates) ? candidates.join("|") : String(candidates || "")]);

  // onError handler on <img> to try next candidate immediately
  const handleImgError = (e) => {
    const ord = buildOrder();
    let nextIdx = (currentIdx >= 0 ? currentIdx + 1 : 0);
    while (nextIdx < ord.length && (!ord[nextIdx] || ord[nextIdx] === currentSrc)) nextIdx += 1;
    if (nextIdx < ord.length) {
      setCurrentIdx(nextIdx);
      setCurrentSrc(ord[nextIdx]);
      // eslint-disable-next-line no-console
      console.debug("[ImageWithFallback] onError -> trying next candidate:", ord[nextIdx]);
    } else {
      // no more candidates, keep fallback
      setCurrentSrc(preferredFallback);
      // eslint-disable-next-line no-console
      console.debug("[ImageWithFallback] onError -> no more candidates, using fallback");
      if (typeof propsOnError === "function") propsOnError(e);
    }
  };

  const handleImgLoad = (e) => {
    // ensure inline styles
    try {
      const el = imgRef.current || e.currentTarget;
      if (el) {
        el.style.width = "100%";
        el.style.height = "100%";
        if (!el.style.objectFit) el.style.objectFit = "cover";
        el.style.display = "block";
      }
    } catch (err) {}
    if (typeof propsOnLoad === "function") propsOnLoad(e);
  };

  return (
    // eslint-disable-next-line jsx-a11y/alt-text
    <img
      ref={imgRef}
      key={currentSrc} // help React remount if the chosen src changes
      src={currentSrc || preferredFallback}
      alt={alt}
      className={className}
      style={{ display: "block", width: "100%", height: "100%", objectFit: "cover", ...style }}
      onError={handleImgError}
      onLoad={handleImgLoad}
      draggable={false}
      {...rest}
    />
  );
}